import React from 'react';
import { Link } from 'react-router-dom';
import { 
  Award,
  ChevronRight,
  Factory,
  Gauge,
  Zap,
  Activity,
  TrendingUp
} from 'lucide-react';
import Carousel from '../components/Carousel';

const officeSlides = [
  {
    src: 'https://customer-assets.emergentagent.com/job_726e721e-6ffb-497e-9efe-ad436fa6993e/artifacts/ihrvxju2_WhatsApp%20Image%202026-06-01%20at%204.39.01%20AM.jpeg',
    caption: 'Usha Automation Corporate Office — Bharthal, Dwarka, New Delhi'
  },
  {
    src: 'https://customer-assets.emergentagent.com/job_726e721e-6ffb-497e-9efe-ad436fa6993e/artifacts/dfd9gcxj_WhatsApp%20Image%202026-06-01%20at%204.39.01%20AM%20%281%29.jpeg',
    caption: 'Our team at work — delivering industrial automation solutions'
  }
];

const Home = () => {
  const industries = [
    { name: 'Oil & Gas', icon: <Factory className="w-8 h-8" /> },
    { name: 'LPG Infrastructure', icon: <Gauge className="w-8 h-8" /> },
    { name: 'Energy & Utilities', icon: <Zap className="w-8 h-8" /> },
    { name: 'Manufacturing', icon: <TrendingUp className="w-8 h-8" /> },
    { name: 'Industrial Plants', icon: <Activity className="w-8 h-8" /> }
  ];

  return (
    <div>
      <section 
        className="relative h-[650px] md:h-[750px] flex items-center justify-center overflow-hidden"
        data-testid="hero-section"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1581091870628-0a8c8e8c5b3d?q=80&w=2070&auto=format&fit=crop)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-slate-900/95 via-slate-900/85 to-[#1E56A0]/70"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="text-left">
              <div className="mb-6">
                <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight text-white mb-4" data-testid="hero-heading">
                  Usha Automation
                </h1>
                <p className="text-2xl md:text-3xl font-semibold text-white mb-3">
                  Complete Industrial Automation & IIoT Solutions
                </p>
                <p className="text-lg md:text-xl text-[#FF7F2A] italic font-medium">
                  Ethical steps towards technology
                </p>
              </div>
              <div className="flex items-center space-x-3 mb-8 bg-white/10 backdrop-blur-sm px-6 py-4 rounded-sm inline-flex">
                <Award className="w-6 h-6 text-[#FF7F2A]" />
                <span className="text-white font-semibold text-lg">5+ Years of Industry Experience</span>
              </div>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link 
                  to="/services" 
                  className="bg-[#FF7F2A] text-white hover:bg-[#e66e1f] rounded-sm px-8 py-4 font-semibold transition-all duration-300 inline-flex items-center justify-center shadow-lg hover:shadow-xl"
                  data-testid="hero-cta-services"
                >
                  Explore Our Services
                  <ChevronRight className="w-5 h-5 ml-2" />
                </Link>
                <Link 
                  to="/contact" 
                  className="border-2 border-white text-white hover:bg-white hover:text-[#1E56A0] rounded-sm px-8 py-4 font-semibold transition-all duration-300 inline-flex items-center justify-center"
                  data-testid="hero-cta-contact"
                >
                  Contact Us
                </Link>
              </div>
            </div>
            <div className="hidden lg:block">
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-white/10 backdrop-blur-sm rounded-sm p-6 hover:bg-white/20 transition-all duration-300">
                  <Factory className="w-10 h-10 text-[#FF7F2A] mb-3" />
                  <h3 className="text-white font-semibold text-lg mb-2">PLC & SCADA</h3>
                  <p className="text-slate-300 text-sm">Advanced automation solutions</p>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-sm p-6 hover:bg-white/20 transition-all duration-300">
                  <Zap className="w-10 h-10 text-[#FF7F2A] mb-3" />
                  <h3 className="text-white font-semibold text-lg mb-2">IIoT Solutions</h3>
                  <p className="text-slate-300 text-sm">Cloud-based monitoring</p>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-sm p-6 hover:bg-white/20 transition-all duration-300">
                  <Activity className="w-10 h-10 text-[#FF7F2A] mb-3" />
                  <h3 className="text-white font-semibold text-lg mb-2">Real-Time Monitoring</h3>
                  <p className="text-slate-300 text-sm">24/7 system oversight</p>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-sm p-6 hover:bg-white/20 transition-all duration-300">
                  <TrendingUp className="w-10 h-10 text-[#FF7F2A] mb-3" />
                  <h3 className="text-white font-semibold text-lg mb-2">OISD Standards</h3>
                  <p className="text-slate-300 text-sm">Designed to OISD norms</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-20 bg-slate-50" data-testid="office-slideshow-section">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-slate-900 mb-3">
              Inside Usha Automation
            </h2>
            <p className="text-base text-slate-600 max-w-2xl mx-auto">
              A glimpse of our office and the team driving reliable industrial automation
            </p>
          </div>
          <Carousel images={officeSlides} testId="home-office-carousel" />
        </div>
      </section>

      <section className="py-20 bg-white" data-testid="trusted-by-section">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
          <p className="text-sm font-medium text-slate-500 uppercase tracking-wider text-center mb-8">Trusted By</p>
          <div className="flex flex-wrap justify-center items-center gap-12 md:gap-16">
            <img 
              src="https://customer-assets.emergentagent.com/job_usha-control/artifacts/jbbj3s0z_image.png" 
              alt="Indian Oil Corporation Limited" 
              className="h-16 w-auto opacity-70 hover:opacity-100 transition-opacity duration-300"
              data-testid="client-logo-iocl"
            />
            <img 
              src="https://customer-assets.emergentagent.com/job_usha-control/artifacts/6wu9xznj_image.png" 
              alt="GAIL India Limited" 
              className="h-16 w-auto opacity-70 hover:opacity-100 transition-opacity duration-300"
              data-testid="client-logo-gail"
            />
            <img 
              src="https://customer-assets.emergentagent.com/job_usha-control/artifacts/0zld8c31_image.png" 
              alt="Hindustan Petroleum Corporation Limited" 
              className="h-16 w-auto opacity-70 hover:opacity-100 transition-opacity duration-300"
              data-testid="client-logo-hpcl"
            />
          </div>
        </div>
      </section>

      <section className="py-20 bg-slate-50" data-testid="industries-section">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-slate-900 mb-6" data-testid="industries-heading">
              Industries We Serve
            </h2>
            <p className="text-base leading-relaxed text-slate-600 max-w-2xl mx-auto">
              Providing automation solutions across diverse industrial sectors
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6">
            {industries.map((industry, index) => (
              <div 
                key={index}
                className="bg-white p-8 rounded-sm text-center hover:shadow-lg transition-all duration-300 group border border-slate-200"
                data-testid={`industry-card-${index}`}
              >
                <div className="text-[#1E56A0] mb-4 flex justify-center group-hover:scale-110 group-hover:text-[#FF7F2A] transition-all duration-300">
                  {industry.icon}
                </div>
                <h3 className="font-semibold text-slate-900 group-hover:text-[#1E56A0] transition-colors">{industry.name}</h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-[#1E56A0]" data-testid="cta-section">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 text-center">
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-white mb-6">
            Ready to Optimize Your Plant?
          </h2>
          <p className="text-lg text-slate-200 mb-8 max-w-2xl mx-auto">
            Let's discuss how our automation solutions can transform your industrial operations
          </p>
          <Link 
            to="/contact" 
            className="inline-flex items-center bg-[#FF7F2A] text-white hover:bg-[#e66e1f] rounded-sm px-8 py-4 font-semibold shadow-md hover:shadow-lg transition-all duration-300"
            data-testid="cta-contact-button"
          >
            Schedule a Consultation
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;
